import { useEffect, useRef, useState } from "react";
import { rtctApi } from "services/rtctApi";
import { liesBetween, preloadIndices } from "utils/capturePlayback";
import { SLOW_FRAME_MS } from "utils/viewerInput";

// Loads the Field Cameras captures as authenticated blobs, decodes them and decides which one is
// on screen.
// - The frame on screen only changes to a frame that is fetched and decoded, so moving the
//   slider never blanks the viewer: the current frame stays until the next one is ready.
// - While scrubbing, a frame that finishes loading is shown only if it lies between the frame
//   on screen and the slider, so frames never appear out of order. While playing only the
//   selected frame is shown; playback advances once it is on screen.
// - Neighbours are fetched once the selected frame is ready: one either side when paused, the
//   next few in play order when playing.
// - Loads outside that window are aborted and their object URLs revoked. The frame on screen and
//   the one before it (which may still be fading out) are kept until they are replaced.

const NO_FRAMES = new Map();
const NOTHING_SHOWN = { now: null, before: null };

/** A capture's identity for caching: a capture whose files change (an original expires) reloads. */
export const frameKey = (capture) =>
  JSON.stringify([
    capture.capture_id,
    (capture.cameras || []).map((c) => [
      c.camera,
      c.file_url,
      c.thumbnail_url,
      !!c.expired,
      !!c.thumbnail_expired,
    ]),
  ]);

function loadFrame(capture, onLoaded) {
  const job = {
    key: frameKey(capture),
    abort: new AbortController(),
    objectUrls: [],
    released: false,
  };
  Promise.allSettled(
    (capture.cameras || []).map(async (c) => {
      if (c.expired && c.thumbnail_expired)
        throw new Error(`Camera ${c.camera}: retention removed this frame`);
      const { blob } = await rtctApi.image(
        c.expired ? c.thumbnail_url : c.file_url,
        job.abort.signal,
      );
      if (job.released) return null;
      const url = URL.createObjectURL(blob);
      job.objectUrls.push(url);
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () =>
          reject(new Error(`Unable to decode Camera ${c.camera}`));
        img.src = url;
      });
      // Decode before the frame is swapped in, so the swap itself never waits for the decoder.
      if (img.decode) await img.decode().catch(() => {});
      return { eye: c.camera, url, img };
    }),
  ).then((rows) => {
    if (job.released) return;
    const loaded = rows
      .filter((r) => r.status === "fulfilled" && r.value)
      .map((r) => r.value);
    onLoaded({
      key: job.key,
      capture,
      images: Object.fromEntries(loaded.map((r) => [r.eye, r.img])),
      urls: Object.fromEntries(loaded.map((r) => [r.eye, r.url])),
      error: rows
        .filter((r) => r.status === "rejected")
        .map((r) => r.reason?.message || String(r.reason))
        .join("; "),
    });
  });
  return job;
}

function release(job) {
  job.released = true;
  job.abort.abort();
  job.objectUrls.forEach((url) => URL.revokeObjectURL(url));
  job.objectUrls = [];
}

export function useCaptureFrames(
  captures,
  index,
  { playing = false, range = null } = {},
) {
  const jobs = useRef(new Map());
  const latest = useRef({});
  const [frames, setFrames] = useState(NO_FRAMES);
  const [shown, setShown] = useState(NOTHING_SHOWN);
  const [slow, setSlow] = useState(false);

  const selected = captures[index] || null;
  const selectedKey = selected ? frameKey(selected) : null;
  const selectedFrame = selectedKey ? frames.get(selectedKey) : undefined;
  const ready = !!selectedFrame;
  const pending = !!selectedKey && !ready;
  const frame = selectedKey
    ? selectedFrame || frames.get(shown.now) || null
    : null;

  useEffect(() => {
    latest.current = { captures, index, selectedKey, playing, frame };
  });

  // The selected frame goes on screen as soon as it is ready (it is already rendered from the
  // cache; this records it so it stays on screen while the next one loads).
  useEffect(() => {
    if (!selectedKey) setShown(NOTHING_SHOWN);
    else if (ready)
      setShown((s) =>
        s.now === selectedKey ? s : { now: selectedKey, before: s.now },
      );
  }, [selectedKey, ready]);

  useEffect(() => {
    const show = (key) =>
      setShown((s) => (s.now === key ? s : { now: key, before: s.now }));
    const loaded = (result) => {
      setFrames((old) => new Map(old).set(result.key, result));
      const now = latest.current;
      if (result.key === now.selectedKey || now.playing || result.error) return;
      const position = (f) =>
        f
          ? now.captures.findIndex(
              (c) => c.capture_id === f.capture.capture_id,
            )
          : -1;
      if (liesBetween(position(now.frame), position(result), now.index))
        show(result.key);
    };
    const ensure = (capture) => {
      const key = frameKey(capture);
      if (!jobs.current.has(key)) jobs.current.set(key, loadFrame(capture, loaded));
      return key;
    };
    const keep = new Set([shown.now, shown.before]);
    if (selected) {
      keep.add(ensure(selected));
      // Neighbours wait for the selected frame so they never compete with it.
      preloadIndices(index, captures.length, { playing, range }).forEach((i) =>
        keep.add(ready ? ensure(captures[i]) : frameKey(captures[i])),
      );
    }
    for (const [key, job] of jobs.current) {
      if (keep.has(key)) continue;
      release(job);
      jobs.current.delete(key);
    }
    setFrames((old) =>
      [...old.keys()].every((key) => keep.has(key))
        ? old
        : new Map([...old].filter(([key]) => keep.has(key))),
    );
  }, [captures, index, selected, ready, playing, range, shown]);

  useEffect(() => {
    if (!pending) {
      setSlow(false);
      return undefined;
    }
    const timer = setTimeout(() => setSlow(true), SLOW_FRAME_MS);
    return () => clearTimeout(timer);
  }, [pending]);

  useEffect(() => {
    const active = jobs.current;
    return () => {
      active.forEach(release);
      active.clear();
    };
  }, []);

  return {
    /** The frame on screen: { key, capture, images, urls, error }, or null before the first one. */
    frame,
    /** The selected capture is the one on screen. */
    ready,
    /** The selected capture is still loading (the previous frame stays on screen). */
    pending,
    /** ...for longer than SLOW_FRAME_MS: worth a small loading badge. */
    slow: pending && slow,
    error: selectedFrame?.error || "",
  };
}
